import * as _ from 'lodash';
import { Cesium } from 'app/cesium';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';
import {
    ImsUserConfig,
    UserGeneratedConfigService,
} from 'app/config/user_generated_config.service';
import { ImsTerrainProvider } from 'app/config/ImsTerrainProvider';
import { UrlInitParams } from 'app/main/url_init_params.service';
import { ILocalStorageService } from 'angular-local-storage';
import angular from 'angular';
import { ConfigService } from 'app/config/config.service';
import { CesiumTerrainProvider, IonResource } from 'cesium';

/**
 * Used to store the terrainProvider, as well as whether or not it is currently enabled or disabled for the map
 */

const LOCAL_STORAGE_KEY_TERRAIN_PROVIDER = 'ImsTerrainProvider';

const DEFAULT_TERRAIN_DETAIL_LEVEL = 13;

type CesiumTerrainProviderFactory = (options: any) => Promise<Cesium.CesiumTerrainProvider>;

type CesiumIonAssetFactory = (id: number) => Promise<Cesium.IonResource>;

interface TerrainServiceOptions {
    // For testing
    cesiumTerrainProviderFactory?: CesiumTerrainProviderFactory;
    // For testing
    cesiumIonAssetFactory?: CesiumIonAssetFactory;
}

interface StoredTerrainProvider {
    uuid: string;
    name: string;
    url?: string;
    cesiumIonId?: number;
}

export class TerrainService {
    _$rootScope: angular.IRootScopeService;
    _mapTerrainEnabled: boolean;
    _cesiumTerrainProvider: Cesium.CesiumTerrainProvider;
    _terrainChangedEventRegistry: CachedEventRegistry;
    _localStorageService;
    _terrainDetailLevel;
    _possibleImsTerrainProviders: ImsTerrainProvider[];
    _locationStatCollection;
    _userGeneratedConfigService: UserGeneratedConfigService;
    _cesiumTerrainProviderFactory: any;
    _cesiumIonAssetFactory: CesiumIonAssetFactory;
    _urlInitParamsService: UrlInitParams;
    _terrainConfigType: string;
    _configService: ConfigService;

    constructor(
        $rootScope: angular.IRootScopeService,
        localStorageService: ILocalStorageService,
        userGeneratedConfigService: UserGeneratedConfigService,
        urlInitParamsService: UrlInitParams,
        configService: ConfigService,
    ) {
        this._$rootScope = $rootScope;
        this._terrainChangedEventRegistry = new CachedEventRegistry();
        this._mapTerrainEnabled = false;
        this._localStorageService = localStorageService;
        void this._setCesiumTerrainProvider({} as ImsTerrainProvider);
    }

    private _updateProvidersAndSetDefaults(config: ImsUserConfig): void {
        this._possibleImsTerrainProviders = config.terrainProviders.mapLayerProviders;
        this._sanitizeLocalStorage();
        this._setDefaultTerrain(config);
    }

    private async _onLoadAndChange(): Promise<void> {
        this._userGeneratedConfigService.onConfigLoad(async (config) => {
            this._mapTerrainEnabled = this._urlInitParamsService.mapTerrainEnabled;
            this._updateProvidersAndSetDefaults(config);
            await this._terrainChangedEventRegistry.notifyListeners();
        });
        this._userGeneratedConfigService.onConfigChange(async (config) => {
            this._updateProvidersAndSetDefaults(config);
            this._refreshCesiumTerrainProvider();
            await this._terrainChangedEventRegistry.notifyListeners();
        });
    }

    private _checkIfLocalStorageProviderHasUuid(storageProvider: StoredTerrainProvider): boolean {
        if (_.has(storageProvider, 'uuid')) {
            return true;
        } else {
            // all providers need to have a UUID
            this._localStorageService.remove(LOCAL_STORAGE_KEY_TERRAIN_PROVIDER);
            return false;
        }
    }

    protected _setDefaultTerrain(config: ImsUserConfig): void {
        const storageProvider = this._localStorageService.get(LOCAL_STORAGE_KEY_TERRAIN_PROVIDER);
        const configProviders = config.terrainProviders.mapLayerProviders;
        if (
            storageProvider &&
            this._verifyImsTerrainProviderExists(storageProvider) &&
            this._checkIfLocalStorageProviderHasUuid(storageProvider)
        ) {
            // We have a valid provider in storage, use it.
            this._setCesiumTerrainProvider(storageProvider).catch((e) => console.error(e));
            return;
        } else if (configProviders.length) {
            // We have configured some terrain providers, use the first one.
            this._setCesiumTerrainProvider(configProviders[0]).catch((e) => console.error(e));
            return;
        } else {
            // There is no terrain, use default.
            this._setEllipsoidTerrainProvider();
        }
    }

    public subscribeChangeOfTerrain(listener: () => void): () => void {
        return this._terrainChangedEventRegistry.addEventListener(listener);
    }

    public setImsTerrainProvider(terrainProviderConfig: ImsTerrainProvider): void {
        // update terrain provider - detects if Cesium Ion is selected and updates accordingly
        this._setCesiumTerrainProvider(terrainProviderConfig).catch((e) => console.error(e));
    }

    public findImsTerrainProviderDetailLevel(terrainProviderConfig?: ImsTerrainProvider): number {
        return DEFAULT_TERRAIN_DETAIL_LEVEL;
    }

    public isBelowMinimumTerrainDetail(): boolean {
        // check the terrain detail level and warn the user if it is low
        return (
            this.findImsTerrainProviderDetailLevel() <
            this._configService.terrainDetailLowWarningLevel
        );
    }

    get possibleImsTerrainProviders(): ImsTerrainProvider[] {
        return this._possibleImsTerrainProviders;
    }

    /**
     * @deprecated prefer use of TerrainProviderContext
     * Get the current cesium terrain provider
     * @returns {Cesium.CesiumTerrainProvider}
     */
    get cesiumTerrainProvider(): Cesium.CesiumTerrainProvider {
        return this._cesiumTerrainProvider;
    }

    /**
     * Get the currently selected config
     * @returns {ImsTerrainProvider}
     */
    get imsTerrainProvider(): ImsTerrainProvider {
        return this._localStorageService.get(LOCAL_STORAGE_KEY_TERRAIN_PROVIDER);
    }

    get terrainDetailLevel(): number {
        return this._terrainDetailLevel;
    }

    get mapTerrainEnabled(): boolean {
        return this._mapTerrainEnabled;
    }

    /**
     * Get the config type for the current terrain
     * @returns {string}
     */
    get terrainConfigType(): string {
        return this._terrainConfigType;
    }

    public async setMapTerrainEnabled(terrainEnabled: boolean): Promise<void> {
        if (terrainEnabled === this._mapTerrainEnabled) {
            return;
        }
        this._mapTerrainEnabled = terrainEnabled;
        this._$rootScope.$emit('terrainService:mapTerrainEnabledSet', this._mapTerrainEnabled);
        await this._terrainChangedEventRegistry.notifyListeners();
    }

    private _setEllipsoidTerrainProvider(): void {
        this._cesiumTerrainProvider = new Cesium.EllipsoidTerrainProvider();
        this._terrainDetailLevel = DEFAULT_TERRAIN_DETAIL_LEVEL;
    }

    private async _setCesiumTerrainProvider(config: ImsTerrainProvider) {
        this._terrainConfigType = 'ion';
        this._cesiumTerrainProvider = await CesiumTerrainProvider.fromUrl(
            IonResource.fromAssetId(1, { accessToken: ConfigService.cesiumIonApiKey }),
            {
                requestWaterMask: false,
                requestVertexNormals: ConfigService.enableTerrainOverlays,
            },
        );
        this._terrainDetailLevel = DEFAULT_TERRAIN_DETAIL_LEVEL;

        await this._terrainChangedEventRegistry.notifyListeners();
        this._$rootScope.$emit('terrainService:mapTerrainEnabledSet', this._mapTerrainEnabled);
    }

    private _refreshCesiumTerrainProvider() {
        const currentTerrainProvider: ImsTerrainProvider = this._localStorageService.get(
            LOCAL_STORAGE_KEY_TERRAIN_PROVIDER,
        );

        if (currentTerrainProvider) {
            this._possibleImsTerrainProviders.forEach((terrainProvider) => {
                if (currentTerrainProvider.uuid === terrainProvider.uuid) {
                    this._setCesiumTerrainProvider(terrainProvider).catch((e) => console.error(e));
                }
            });
        }
    }

    private _verifyImsTerrainProviderExists(imsTerrainProvider: ImsTerrainProvider): boolean {
        return !!this._findImsTerrainProvider(imsTerrainProvider);
    }

    /**
     * Find a terrain provider in our config that matches the passed in
     * terrainProviderConfig object. We only match on the url if it exists, or the cesiumIonId if it exists
     * @param {ImsTerrainProvider} imsTerrainProvider
     * @private
     */
    private _findImsTerrainProvider(imsTerrainProvider: ImsTerrainProvider): StoredTerrainProvider {
        if (imsTerrainProvider) {
            return this._possibleImsTerrainProviders.find((t) => {
                return t.uuid === imsTerrainProvider.uuid;
            });
        } else {
            return null;
        }
    }

    /**
     * Does the provider in local storage match what's in our configuration? If not, remove it
     */
    private _sanitizeLocalStorage() {
        const terrainProvider = this._findImsTerrainProvider(
            this._localStorageService.get(LOCAL_STORAGE_KEY_TERRAIN_PROVIDER),
        );
        if (!terrainProvider) {
            this._localStorageService.remove(LOCAL_STORAGE_KEY_TERRAIN_PROVIDER);
        }
    }
}
